import React, { PropsWithChildren, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
    Extrapolation,
    SharedValue,
    interpolate,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { ParamListBase, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import BottomSheet from '@gorhom/bottom-sheet';

import { useLocalFlagsStore } from '../devsettings/hooks/use-dev-settings';
import { useSbkPickbar } from '../lobby-sbk/components/SbkPickbarProvider';

type FullScreenTransitionContextValue = {
    animatedPosition: SharedValue<number>;
    pageAnim: SharedValue<number>;
    selectedTabSharedValue: SharedValue<number>;
    animatedIndex: SharedValue<number>;
    sheetMountAnimationProgress: SharedValue<number>;
    isSheetMountCompleted: SharedValue<boolean>;
    snapPointIndex: number;
    setSnapPointIndex: (index: number) => void;
    mountingFadeIn: ReturnType<typeof useAnimatedStyle>;
    bottomSheetCircularPagerRef: React.MutableRefObject<BottomSheet | null>;
};

const FullScreenPagerTransitionContext = React.createContext<FullScreenTransitionContextValue | undefined>(undefined);

export const MOUNT_FADE_IN_DURATION = 800;

const ANIMATION_ENABLED = Platform.OS === 'ios';

export type FullScreenPagerTransitionProviderProps = {
    initialIndex?: number;
};

export const FullScreenPagerTransitionProvider: React.FC<PropsWithChildren<FullScreenPagerTransitionProviderProps>> = ({
    children,
    initialIndex,
}) => {
    const navigation = useNavigation<NativeStackNavigationProp<ParamListBase>>();
    const { localFlags } = useLocalFlagsStore();
    const slowAnimations = localFlags?.useSlowAnimations?.enabled;
    const animatedPosition = useSharedValue(0);
    const pageAnim = useSharedValue(initialIndex ?? 0);
    const animatedIndex = useSharedValue(0);
    const sheetMountAnimationProgress = useSharedValue(ANIMATION_ENABLED ? -1 : 0);
    const selectedTabSharedValue = useSharedValue(-1);

    const [snapPointIndex, setSnapPointIndex] = useState(0);

    const isSheetMountCompleted = useSharedValue(ANIMATION_ENABLED ? false : true);

    const bottomSheetCircularPagerRef = useRef<BottomSheet>(null);

    const mountDuration = slowAnimations ? MOUNT_FADE_IN_DURATION * 4 : MOUNT_FADE_IN_DURATION;

    const { pickbarTransitionValue: sbkPickbarTransitionValue } = useSbkPickbar();

    // sync pickbar transition with sheet index changes
    useAnimatedReaction(
        () => animatedIndex.value,
        (next, prev) => {
            if (next !== prev) {
                if (!isSheetMountCompleted.value) {
                    return;
                }
                sbkPickbarTransitionValue.value = next;
            }
        }
    );

    // start the fade when the screen is on screen, not on mount
    useLayoutEffect(() => {
        if (!ANIMATION_ENABLED) {
            return;
        }
        const unsubscribe = navigation.addListener('transitionEnd', event => {
            if (event.data.closing) {
                return;
            }
            unsubscribe();
            sheetMountAnimationProgress.value = withTiming(0, { duration: mountDuration }, () => {
                isSheetMountCompleted.value = true;
            });
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mountingFadeIn = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {
                opacity: interpolate(sheetMountAnimationProgress.value, [-1, 0], [0, 1], Extrapolation.CLAMP),
            };
        }

        return {
            opacity: interpolate(animatedIndex.value, [-1, 0], [0, 1], Extrapolation.CLAMP),
        };
    });

    const value: FullScreenTransitionContextValue = useMemo(() => {
        return {
            animatedPosition,
            animatedIndex,
            snapPointIndex,
            setSnapPointIndex,
            sheetMountAnimationProgress,
            mountingFadeIn,
            isSheetMountCompleted,
            selectedTabSharedValue,
            pageAnim,
            bottomSheetCircularPagerRef,
        };
    }, [
        animatedPosition,
        animatedIndex,
        snapPointIndex,
        sheetMountAnimationProgress,
        mountingFadeIn,
        isSheetMountCompleted,
        selectedTabSharedValue,
        pageAnim,
    ]);

    return (
        <FullScreenPagerTransitionContext.Provider value={value}>{children}</FullScreenPagerTransitionContext.Provider>
    );
};

export const useFullScreenPagerTransition = () => {
    const context = useContext(FullScreenPagerTransitionContext);
    if (!context) {
        throw new Error('useFullScreenPagerTransition must be used within a FullScreenPagerTransitionProvider');
    }
    return context;
};
